import { z } from 'zod'
import type { NotificatieEventTypeKey } from './notificaties'
import type { PushSubscriptionDTO } from './notificaties'

/**
 * batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4) — de UNIFORME
 * opvolg-flow voor klantverzoeken: soorten, statussen, DTO's en de gedeelde
 * Zod-schema's (API valideert er mee, de formulieren ook - één bron).
 *
 * Elke klant-geïnitieerde actie is een werkvoorraad-item (`KlantVerzoekOpvolging`)
 * met een status uit `ClientPortalRequestStatus`. De bron (een `ClientPortalRequest`,
 * gereedschapsverzoek, monteur-reactie, sluitingsdag of beoordeling) blijft
 * bestaan; het opvolg-item is de gedeelde laag die postvak, werkvoorraad,
 * escalatie én de klant-status voedt.
 */

// ─── Soorten ─────────────────────────────────────────────────────────────────

export const KLANTVERZOEK_SOORTEN = [
  'STORING',
  'EXTRA_INZET',
  'KLACHT',
  'VRAAG',
  'OFFERTE',
  'PLANNING_WIJZIGING',
  'GEGEVENS_WIJZIGING',
  'CONTACTGEGEVENS',
  'MONTEUR_PROFIEL',
  'GEREEDSCHAP',
  'MONTEURKEUZE',
  'SLUITINGSDAG',
  'BEOORDELING',
] as const
export type KlantVerzoekSoort = (typeof KLANTVERZOEK_SOORTEN)[number]

/** Backoffice-labels (de klant-app vertaalt via next-intl, namespace `verzoeken`). */
export const KLANTVERZOEK_SOORT_LABELS: Record<KlantVerzoekSoort, string> = {
  STORING: 'Storing',
  EXTRA_INZET: 'Extra inzet',
  KLACHT: 'Klacht',
  VRAAG: 'Vraag',
  OFFERTE: 'Offerte-aanvraag',
  PLANNING_WIJZIGING: 'Planningswijziging',
  GEGEVENS_WIJZIGING: 'Wijziging klantgegevens',
  CONTACTGEGEVENS: 'Contactgegevens monteur',
  MONTEUR_PROFIEL: 'Monteursprofiel',
  GEREEDSCHAP: 'Gereedschap of materieel',
  MONTEURKEUZE: 'Reactie op monteur',
  SLUITINGSDAG: 'Sluitingsdag',
  BEOORDELING: 'Beoordeling',
}

/**
 * Per soort de eigen event-key (spec §2.7: "elk krijgt een eigen event-key").
 * De registry-`TEMPLATES` is totaal over alle keys, dus een nieuwe soort zonder
 * template is een typefout.
 */
export const KLANTVERZOEK_SOORT_EVENT_KEY = {
  STORING: 'KLANTVERZOEK_STORING',
  EXTRA_INZET: 'KLANTVERZOEK_EXTRA_INZET',
  KLACHT: 'KLANTVERZOEK_KLACHT',
  VRAAG: 'KLANTVERZOEK_VRAAG',
  OFFERTE: 'KLANTVERZOEK_OFFERTE',
  PLANNING_WIJZIGING: 'KLANTVERZOEK_PLANNING_WIJZIGING',
  GEGEVENS_WIJZIGING: 'KLANTVERZOEK_GEGEVENS_WIJZIGING',
  CONTACTGEGEVENS: 'KLANTVERZOEK_CONTACTGEGEVENS',
  MONTEUR_PROFIEL: 'KLANTVERZOEK_MONTEUR_PROFIEL',
  GEREEDSCHAP: 'KLANTVERZOEK_GEREEDSCHAP',
  MONTEURKEUZE: 'KLANTVERZOEK_MONTEURKEUZE',
  SLUITINGSDAG: 'KLANTVERZOEK_SLUITINGSDAG',
  BEOORDELING: 'KLANTVERZOEK_BEOORDELING',
} as const satisfies Record<KlantVerzoekSoort, NotificatieEventTypeKey>

export const KLANTVERZOEK_ONBEANTWOORD_EVENT_KEY =
  'KLANTVERZOEK_ONBEANTWOORD' satisfies NotificatieEventTypeKey

/**
 * Soorten die TER KENNISNAME zijn: er valt niets af te handelen (een ingevulde
 * beoordeling is feedback). Ze worden direct als AFGEROND geregistreerd, zijn
 * NOTIFICATIE i.p.v. ACTIE en vallen buiten de onbeantwoord-escalatie.
 */
export const KLANTVERZOEK_INFORMATIEVE_SOORTEN: readonly KlantVerzoekSoort[] = ['BEOORDELING']

export function isKlantVerzoekSoortInformatief(soort: KlantVerzoekSoort): boolean {
  return KLANTVERZOEK_INFORMATIEVE_SOORTEN.includes(soort)
}

/** Alle klantverzoek-event-keys (de per-soort keys + de escalatie). */
export const KLANTVERZOEK_EVENT_KEYS: readonly NotificatieEventTypeKey[] = [
  ...Object.values(KLANTVERZOEK_SOORT_EVENT_KEY),
  KLANTVERZOEK_ONBEANTWOORD_EVENT_KEY,
]

/** Is dit event een klantverzoek-event (per-soort of de escalatie)? */
export function isKlantVerzoekEventKey(key: string | null | undefined): boolean {
  return key != null && (KLANTVERZOEK_EVENT_KEYS as readonly string[]).includes(key)
}

/**
 * Is dit een PER-SOORT klantverzoek-event (bronEntiteitId = precies één
 * opvolging)? De gebundelde escalatie valt hier bewust buiten: die gaat over
 * meerdere verzoeken en linkt naar het overzicht, niet naar één afhandel-blok.
 */
export function isKlantVerzoekSoortEventKey(key: string | null | undefined): boolean {
  return (
    key != null &&
    (Object.values(KLANTVERZOEK_SOORT_EVENT_KEY) as readonly string[]).includes(key)
  )
}

/** `NotificatieEvent.module` == `InboxItem.bronModule` van alle klantverzoek-events. */
export const KLANTVERZOEK_BRON_MODULE = 'klantverzoeken'

/**
 * Spiegelt de Prisma-enum `ClientPortalRequestType`. De api-drift-test
 * (`klantverzoek-request-type-drift.test.ts`) faalt zodra de enum een waarde
 * krijgt die hier ontbreekt: het oude verzamel-event vuurde voor élk type en
 * dat mag de per-soort splitsing niet stil verliezen.
 */
export const CLIENT_PORTAL_REQUEST_TYPES = [
  'STORING',
  'EXTRA_WERK',
  'KLACHT',
  'VRAAG',
  'OFFERTE_AANVRAAG',
  'EXTRA_DIENST',
  'PLANNING_WIJZIGING',
  'PROFIEL_WIJZIGING',
  'CONTACTGEGEVENS_OPVRAGEN',
  'MONTEUR_PROFIEL',
] as const
export type ClientPortalRequestTypeKey = (typeof CLIENT_PORTAL_REQUEST_TYPES)[number]

/** Het `ClientPortalRequestType` -> soort; TOTAAL over de enum (zie hierboven). */
export const KLANTVERZOEK_SOORT_VAN_REQUEST_TYPE: Record<ClientPortalRequestTypeKey, KlantVerzoekSoort> = {
  STORING: 'STORING',
  EXTRA_WERK: 'EXTRA_INZET',
  EXTRA_DIENST: 'EXTRA_INZET',
  KLACHT: 'KLACHT',
  VRAAG: 'VRAAG',
  OFFERTE_AANVRAAG: 'OFFERTE',
  PLANNING_WIJZIGING: 'PLANNING_WIJZIGING',
  PROFIEL_WIJZIGING: 'GEGEVENS_WIJZIGING',
  CONTACTGEGEVENS_OPVRAGEN: 'CONTACTGEGEVENS',
  MONTEUR_PROFIEL: 'MONTEUR_PROFIEL',
}

// ─── Bron-modellen ───────────────────────────────────────────────────────────

export const KLANTVERZOEK_BRON_MODELLEN = [
  'ClientPortalRequest',
  'KlantGereedschapVerzoek',
  'KlantMonteurReactie',
  'ClientClosedPeriod',
  'ClientBeoordeling',
] as const
export type KlantVerzoekBronModel = (typeof KLANTVERZOEK_BRON_MODELLEN)[number]

/** Welk bron-model bij een soort hoort (één model per soort). */
export const KLANTVERZOEK_SOORT_BRON_MODEL: Record<KlantVerzoekSoort, KlantVerzoekBronModel> = {
  STORING: 'ClientPortalRequest',
  EXTRA_INZET: 'ClientPortalRequest',
  KLACHT: 'ClientPortalRequest',
  VRAAG: 'ClientPortalRequest',
  OFFERTE: 'ClientPortalRequest',
  PLANNING_WIJZIGING: 'ClientPortalRequest',
  GEGEVENS_WIJZIGING: 'ClientPortalRequest',
  CONTACTGEGEVENS: 'ClientPortalRequest',
  MONTEUR_PROFIEL: 'ClientPortalRequest',
  GEREEDSCHAP: 'KlantGereedschapVerzoek',
  MONTEURKEUZE: 'KlantMonteurReactie',
  SLUITINGSDAG: 'ClientClosedPeriod',
  BEOORDELING: 'ClientBeoordeling',
}

/**
 * Same-origin pad in de KLANT-app naar de bron van een verzoek (voor de
 * "Mijn verzoeken"-lijst). Nooit een API-URL.
 */
export function klantVerzoekKlantPad(soort: KlantVerzoekSoort, bronId: string): string {
  switch (KLANTVERZOEK_SOORT_BRON_MODEL[soort]) {
    case 'ClientPortalRequest':
      return soort === 'MONTEUR_PROFIEL'
        ? '/monteursprofielen'
        : `/aanvragen/${encodeURIComponent(bronId)}`
    case 'KlantGereedschapVerzoek':
      return '/gereedschap'
    case 'KlantMonteurReactie':
      return '/monteurkeuze'
    case 'ClientClosedPeriod':
      return '/sluitingsdagen'
    case 'ClientBeoordeling':
      return '/beoordelingen'
  }
}

/** Backoffice-pad naar de bron (het "open de bron"-linkje in de werkvoorraad). */
export function klantVerzoekBackofficeBronPad(input: {
  soort: KlantVerzoekSoort
  bronId: string
  clientId: string
}): string {
  switch (KLANTVERZOEK_SOORT_BRON_MODEL[input.soort]) {
    case 'ClientPortalRequest':
      return `/klantenportaal/aanvragen/${encodeURIComponent(input.bronId)}`
    case 'KlantGereedschapVerzoek':
      return '/klantenportaal/gereedschapsverzoeken'
    case 'KlantMonteurReactie':
      return '/klantenportaal/monteur-reacties'
    case 'ClientClosedPeriod':
      return `/relaties/${encodeURIComponent(input.clientId)}?tab=portaal`
    case 'ClientBeoordeling':
      return '/klantenportaal/beoordelingen'
  }
}

// ─── Statussen ───────────────────────────────────────────────────────────────

/** Spiegelt de Prisma-enum `ClientPortalRequestStatus` (en `KLANTVERZOEK_OPVOLG_STATUSSEN` in utils). */
export const KLANTVERZOEK_OPVOLG_STATUSSEN = [
  'INGEDIEND',
  'IN_BEHANDELING',
  'AFGEROND',
  'AFGEWEZEN',
  'INGETROKKEN',
] as const
export type KlantVerzoekOpvolgStatus = (typeof KLANTVERZOEK_OPVOLG_STATUSSEN)[number]

export const KLANTVERZOEK_OPVOLG_STATUS_LABELS: Record<KlantVerzoekOpvolgStatus, string> = {
  INGEDIEND: 'Nieuw',
  IN_BEHANDELING: 'In behandeling',
  AFGEROND: 'Afgehandeld',
  AFGEWEZEN: 'Afgewezen',
  INGETROKKEN: 'Ingetrokken door klant',
}

/** Badge-varianten van `@streep/ui-fp` (de backoffice-Badge). */
export const KLANTVERZOEK_OPVOLG_STATUS_BADGE: Record<
  KlantVerzoekOpvolgStatus,
  'default' | 'secondary' | 'warning' | 'success' | 'danger'
> = {
  INGEDIEND: 'warning',
  IN_BEHANDELING: 'default',
  AFGEROND: 'success',
  AFGEWEZEN: 'danger',
  INGETROKKEN: 'secondary',
}

/** De statussen die de backoffice zelf kan zetten (INGETROKKEN is klant-only). */
export const KLANTVERZOEK_BESLUIT_STATUSSEN = ['IN_BEHANDELING', 'AFGEROND', 'AFGEWEZEN'] as const
export type KlantVerzoekBesluitStatus = (typeof KLANTVERZOEK_BESLUIT_STATUSSEN)[number]

export const KLANTVERZOEK_REDEN_MIN = 3

/**
 * Het besluit op een klantverzoek. Afwijzen vereist een reden (spec §2.9); de
 * reden is KLANT-zichtbaar (hij ziet 'm in het portaal). `interneNotitie`
 * blijft altijd achter de backoffice.
 */
export const klantVerzoekOpvolgBesluitSchema = z
  .object({
    status: z.enum(KLANTVERZOEK_BESLUIT_STATUSSEN),
    reden: z.string().trim().max(2000).optional().nullable(),
    interneNotitie: z.string().trim().max(2000).optional().nullable(),
  })
  .refine((v) => v.status !== 'AFGEWEZEN' || (v.reden ?? '').trim().length >= KLANTVERZOEK_REDEN_MIN, {
    message: 'Geef een reden bij het afwijzen; de klant ziet die terug in het portaal',
    path: ['reden'],
  })
export type KlantVerzoekOpvolgBesluitInput = z.infer<typeof klantVerzoekOpvolgBesluitSchema>

export const klantVerzoekLijstQuerySchema = z.object({
  status: z.enum(KLANTVERZOEK_OPVOLG_STATUSSEN).optional(),
  soort: z.enum(KLANTVERZOEK_SOORTEN).optional(),
  clientId: z.string().trim().min(1).optional(),
  /** Alleen geëscaleerde (te lang onbeantwoorde) verzoeken (`1`/`true`). */
  escalatie: z
    .union([z.boolean(), z.enum(['1', 'true', '0', 'false'])])
    .optional()
    .transform((v) => (v === true || v === '1' || v === 'true' ? true : undefined)),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type KlantVerzoekLijstQuery = z.infer<typeof klantVerzoekLijstQuerySchema>

// ─── DTO's (backoffice) ──────────────────────────────────────────────────────

export interface KlantVerzoekWerkvoorraadRij {
  id: string
  soort: KlantVerzoekSoort
  soortLabel: string
  status: KlantVerzoekOpvolgStatus
  samenvatting: string
  klant: { id: string; name: string; clientNumber: string | null }
  ingediendOp: string
  /** Weergavenaam van het klant-account dat het indiende (null bij systeem/onbekend). */
  ingediendDoor: string | null
  behandeldDoor: string | null
  inBehandelingOp: string | null
  afgehandeldOp: string | null
  /** Klant-zichtbare reden/toelichting bij het besluit. */
  reden: string | null
  /** Interne notitie - nooit in een klant-DTO. */
  interneNotitie: string | null
  escalatieOp: string | null
  bronModel: KlantVerzoekBronModel
  bronId: string
  bronPad: string
  eventTypeKey: string | null
}

export interface KlantVerzoekWerkvoorraadMeta {
  total: number
  page: number
  pageSize: number
  /** Server-side tellers over de VOLLEDIGE set (nooit over de geladen pagina). */
  tellers: Record<KlantVerzoekOpvolgStatus, number>
  /** Aantal geëscaleerde verzoeken in de gefilterde set (server-side geteld). */
  escalatie: number
  escalatieUren: number
}

export interface KlantVerzoekStatusEventDTO {
  id: string
  vanStatus: KlantVerzoekOpvolgStatus | null
  naarStatus: KlantVerzoekOpvolgStatus
  door: string | null
  doorKlant: boolean
  reden: string | null
  createdAt: string
}

export interface KlantVerzoekWerkvoorraadDetail extends KlantVerzoekWerkvoorraadRij {
  historie: KlantVerzoekStatusEventDTO[]
}

// ─── Instellingen (ontvangersgroep + escalatie) ─────────────────────────────

export const KLANTVERZOEK_INSTELLING_KEYS = {
  ONTVANGER_USER_IDS: 'klantverzoeken.ontvangerUserIds',
  ESCALATIE_USER_IDS: 'klantverzoeken.escalatieUserIds',
  ESCALATIE_UREN: 'klantverzoeken.escalatieUren',
} as const

export const klantVerzoekInstellingenSchema = z.object({
  /** De ontvangersgroep "Klantverzoeken" (User.id's). Leeg = terugval intake-team. */
  ontvangerUserIds: z.array(z.string().trim().min(1)).max(50),
  /** Wie de gebundelde escalatie krijgt. Leeg = managers, anders de groep. */
  escalatieUserIds: z.array(z.string().trim().min(1)).max(50),
  /** Onbeantwoord na zoveel uur -> escalatie (default 24). */
  escalatieUren: z.number().int().min(1).max(168),
})
export type KlantVerzoekInstellingenInput = z.infer<typeof klantVerzoekInstellingenSchema>

export interface KlantVerzoekOntvangerDTO {
  userId: string
  naam: string
  email: string
  rol: string
  actief: boolean
  /** 0 = "geen push-device geregistreerd" (zichtbare status, geen stille no-op). */
  pushDevices: number
}

export interface KlantVerzoekInstellingenDTO extends KlantVerzoekInstellingenInput {
  ontvangers: KlantVerzoekOntvangerDTO[]
  escalatieOntvangers: KlantVerzoekOntvangerDTO[]
  /** Wie de melding NU effectief krijgt als de groep leeg is (label van de terugval). */
  terugvalOntvangersLabel: string
  terugvalEscalatieLabel: string
}

// ─── DTO's (klant-app) ───────────────────────────────────────────────────────

/**
 * Wat de klant ziet in "Mijn verzoeken". Veld-allowlist: geen interne notitie,
 * geen behandelaar, geen escalatie-informatie.
 */
export interface KlantVerzoekKlantRij {
  id: string
  soort: KlantVerzoekSoort
  status: KlantVerzoekOpvolgStatus
  samenvatting: string
  ingediendOp: string
  afgehandeldOp: string | null
  /** Alleen bij AFGEROND/AFGEWEZEN: de klant-zichtbare reden/toelichting. */
  reden: string | null
  /** Same-origin pad in de klant-app naar de bron. */
  bronPad: string
}

export interface KlantVerzoekKlantMeta {
  total: number
  page: number
  pageSize: number
  open: number
}

// ─── Push-status (gebruikersinstellingen) ────────────────────────────────────

/** `GET /notificaties/push/status` - eigen push-devices van de ingelogde gebruiker. */
export interface PushStatusDTO {
  /** Staat er een VAPID-sleutel op de server? Zonder die kan niemand push krijgen. */
  vapidGeconfigureerd: boolean
  devices: PushSubscriptionDTO[]
}
